import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useSession } from '@clerk/clerk-react';
import {
  ArrowLeft,
  ArrowRight,
  WrenchIcon,
  Star,
  Wall,
  Calendar,
  ActivityIcon,
  PencilSimple,
  EnvelopeSimple,
  ArrowCounterClockwise,
  GitMerge,
  User,
  Paperclip,

  ArrowsOut,
  Trash,
  Buildings,
  Cube,
  Tag,
  Plus,
  X,
  Warning,
} from '@phosphor-icons/react';
import { Button } from 'components/ui/button';
import { Textarea } from 'components/ui/textarea';
import { Label } from 'components/ui/label';
import { Switch } from 'components/ui/switch';
import { CompanyContactPicker } from 'shared/components/contact';
import { CompanyContactPerson } from 'shared/types/contact';
import {
  resolveSupplierRecipientEmail,
  toCompanyContactPerson,
} from 'shared/utils/companyContact';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { toast } from 'react-toastify';
import { useUsers } from 'shared/contexts/users.context';
import { getUserName, getUserImageUrlById } from 'shared/utils/users';
import { UniversalPickerModal } from 'shared/components/UniversalPickerModal';
import {
  RenoTicket,
  RenoTicketStatus,
  RenoTicketPriority,
  RenoTicketSource,
  RenoMessageType,
  STATUS_COLORS,
  getRenoCommunicationCode,
} from './types/renoTicket';
import { RenoPhaseHistoryTimeline } from './components/RenoPhaseHistoryTimeline';
import { RenoActionNotesSection } from './components/RenoActionNotesSection';
import { ReopenReasonModal } from './components/ReopenReasonModal';
import { MergeRenoTicketModal } from './components/MergeRenoTicketModal';
import { RenoLinkedTicketsSection } from './components/RenoLinkedTicketsSection';
import { RenoTicketDocumentsSection } from './components/RenoTicketDocumentsSection';
import { RenoTicketPhotosSection } from './components/RenoTicketPhotosSection';
import { RenoMassMailModal } from './components/RenoMassMailModal';
import { RenoRecipientSelect, RecipientOption } from './components/RenoRecipientSelect';
import { RenoMessageTypeSelect } from './components/RenoMessageTypeSelect';
import { AddedRecipient } from './components/RenoRecipientSearch';
import { SendResultsModal } from 'screens/mass-mail';
import { buildRenoSendReport, RenoSendReport } from './utils/buildRenoSendResults';
import { sendRenoTicketEmail } from './utils/sendRenoEmail';
import { RenoAssignAndNotifyModal } from './components/RenoAssignAndNotifyModal';
import { RenoTagsInput } from './components/RenoTagsInput';
import { RenoMailInfoNotice } from './components/RenoMailInfoNotice';
import { RenoFinancialDocumentsSection } from './components/RenoFinancialDocumentsSection';
import { TenantBalanceBadge, useTenantBalances, tenantBalanceKey } from 'shared/components/tenant';
import { renoMailBlockReason } from './utils/renoMailBlockReason';
import { RenoEmailItem, RenoEmailAttachment } from './components/RenoEmailDetailModal';
import * as Sentry from '@sentry/react';
import { TranslatePreview } from 'shared/components/TranslatePreview';
import { filterRenoEmailsByChannel, renoEmailMatchesChannel } from './utils/renoEmailChannel';
import {
  buildRenoNameResolver,
  describeRenoEmailFromTo,
} from './utils/renoEmailParticipants';
import { AttachmentViewer } from 'shared/components/email/AttachmentViewer';



const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric' });

const formatDateInput = (dateString?: string) => {
  if (!dateString) return '';
  return new Date(dateString).toISOString().split('T')[0];
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'Hoog': return 'bg-red-100 text-red-700 border-red-300';
    case 'Gemiddeld': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    case 'Laag': return 'bg-green-100 text-green-700 border-green-300';
    default: return 'bg-gray-100 text-gray-700 border-gray-300';
  }
};

export default function RenoHelpdeskTicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { session } = useSession();
  const { users } = useUsers();

  // Communicatiekanaal: leverancier (RS) of huurder (RT). Stuurbaar via ?channel=.
  const channel: 'supplier' | 'tenant' = searchParams.get('channel') === 'tenant' ? 'tenant' : 'supplier';
  const setChannel = (c: 'supplier' | 'tenant') => {
    const next = new URLSearchParams(searchParams);
    next.set('channel', c);
    setSearchParams(next, { replace: true });
  };
  const [ticket, setTicket] = useState<RenoTicket | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [isReopenModalOpen, setIsReopenModalOpen] = useState(false);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [isSendEmailOpen, setIsSendEmailOpen] = useState(false);
  // Van buitenaf te openen, zodat de waarschuwing over een ontbrekend e-mailadres
  // rechtstreeks naar het paneel leidt waar je dat adres kan aanvullen.
  const [isRecipientSelectOpen, setIsRecipientSelectOpen] = useState(false);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState<'project' | 'part' | 'assignee' | 'reporter' | 'supplier' | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<RenoEmailItem | null>(null);

  // Mail-ontvangers (multi-select naast de stuurknop).
  const [recipientsData, setRecipientsData] = useState<{
    contactPerson: { id: number; name: string; email: string } | null;
    unitTenants: { id: number; name: string; email: string }[];
    buildingTenants: { id: number; name: string; email: string }[];
    suppliers: { id: number; name: string; email: string }[];
  } | null>(null);
  const [selectedRecipientKeys, setSelectedRecipientKeys] = useState<string[]>([]);
  // Type van de mail die je nu opstelt (Offerte, Factuur, …). Wordt per mail bewaard;
  // het tickettype dient enkel als startwaarde, want dat kan later nog wijzigen.
  const [mailMessageType, setMailMessageType] = useState<RenoMessageType | null>(null);
  // Vrij toegevoegde ontvangers: contactpersonen/firma's die niet aan dit ticket,
  // het renovatieproject of het projectonderdeel gekoppeld zijn.
  const [extraRecipients, setExtraRecipients] = useState<AddedRecipient[]>([]);
  // Verzendrapport na het versturen van een mail (composer is dan al gesloten).
  const [sendReport, setSendReport] = useState<RenoSendReport | null>(null);

  const fetchTicket = useCallback(async () => {
    if (!session || !id) return;
    setIsLoading(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/reno-helpdesk/tickets/${id}`, { method: 'GET' }, session);
      if (!res.ok) throw new Error('Ticket niet gevonden');
      const data = await res.json();
      setTicket(data);

      // Ongelezen ontvangen mails op gelezen zetten, zodat de rode markering in de
      // ticketlijst verdwijnt zodra je het ticket bekeken hebt (zoals bij de helpdesk).
      const heeftOngelezen = (data?.emails ?? []).some(
        (email: any) => email?.direction === 'received' && !email?.isRead,
      );
      if (heeftOngelezen) {
        try {
          await fetchWithAuth(
            `${API_URL}/reno-helpdesk/tickets/${id}/emails/mark-read`,
            { method: 'PUT' },
            session,
          );
        } catch (error) {
          // De gebruiker heeft de mails wél gezien; enkel de markering blijft staan.
          Sentry.captureException(error, {
            tags: { module: 'reno-helpdesk', action: 'markEmailsRead' },
            extra: { ticketId: id },
          });
        }
      }
    } catch (err: any) {
      toast.error(err.message);
      navigate('/renotickets');
    } finally {
      setIsLoading(false);
    }
  }, [session, id, navigate]);

  useEffect(() => { fetchTicket(); }, [fetchTicket]);

  // Ontvangers ophalen (gebouw/unit-huurders, leveranciers, contactpersoon).
  const fetchRecipients = useCallback(async () => {
    if (!session || !id) return;
    try {
      const res = await fetchWithAuth(
        `${API_URL}/reno-helpdesk/tickets/${id}/recipients`,
        { method: 'GET' },
        session,
      );
      if (res.ok) setRecipientsData(await res.json());
    } catch {
      /* ontvangers zijn optioneel: de mailmodule werkt ook met vrij zoeken */
    }
  }, [session, id]);

  useEffect(() => { fetchRecipients(); }, [fetchRecipients]);

  // Typekeuze voorvullen met het tickettype zodra het ticket geladen is. Enkel bij een
  // ander ticket, zodat een handmatige keuze niet overschreven wordt bij elke refetch.
  useEffect(() => {
    setMailMessageType((ticket?.messageType as RenoMessageType | null) ?? null);
    // messageType bewust niet in de dependencies: enkel bij een ander ticket voorvullen,
    // anders overschrijft elke refetch (bv. na het versturen) de handmatige keuze.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.id]);

  // Selectie resetten bij kanaalwissel (huurder/leverancier), maar vrij toegevoegde
  // ontvangers behouden — die horen niet bij één kanaal.
  useEffect(() => {
    setSelectedRecipientKeys((prev) => prev.filter((k) => k.startsWith('extra-')));
  }, [channel]);

  // Enkel de mails van het gekozen kanaal: een leveranciersmail (RS) hoort niet
  // onder het huurdergedeelte en omgekeerd. Zie utils/renoEmailChannel.ts voor de
  // regel; mails die aan geen kanaal toegewezen kunnen worden blijven in beide staan.
  const channelEmails = useMemo(
    () =>
      filterRenoEmailsByChannel(((ticket as any)?.emails ?? []) as RenoEmailItem[], channel).sort(
        (a: any, b: any) =>
          new Date(b.receivedDateTime).getTime() - new Date(a.receivedDateTime).getTime(),
      ),
    [ticket, channel],
  );

  // Namen bij e-mailadressen, zodat het mailoverzicht "Elektro MS" toont i.p.v.
  // "info@electro-ms.be". Onbekende adressen blijven als adres staan.
  const resolveRecipientName = useMemo(
    () =>
      buildRenoNameResolver([
        recipientsData?.contactPerson,
        ...(recipientsData?.suppliers ?? []),
        ...(recipientsData?.unitTenants ?? []),
        ...(recipientsData?.buildingTenants ?? []),
        ...extraRecipients.map((r) => ({ name: r.name, email: r.email })),
        ...(ticket?.supplierAssignments ?? []).map((a) => ({
          name: a.supplier?.name,
          email: a.supplier?.email,
        })),
        // Ook de gekozen contactpersonen: mails naar hun adres moeten in het
        // overzicht hun naam tonen i.p.v. het ruwe e-mailadres.
        ...(ticket?.supplierAssignments ?? [])
          .filter((a) => a.contact?.email)
          .map((a) => ({
            name: `${a.contact!.firstName ?? ''} ${a.contact!.lastName ?? ''}`.trim(),
            email: a.contact!.email,
          })),
        ticket?.reporter
          ? {
              name: `${ticket.reporter.firstName ?? ''} ${ticket.reporter.lastName ?? ''}`.trim(),
              email: ticket.reporter.email,
            }
          : null,
      ]),
    [recipientsData, extraRecipients, ticket?.supplierAssignments, ticket?.reporter],
  );

  /**
   * Bijlagen van de geopende mail. Ze staan niet altijd in de databank: oudere reno-mails
   * werden zonder bijlagemetadata bewaard. Staat `hasAttachments` aan zonder lijst, dan
   * halen we ze bij het openen uit het postvak (zelfde aanpak als het helpdesk-verloop).
   */
  const [fetchedAttachments, setFetchedAttachments] = useState<Record<string, RenoEmailAttachment[]>>({});
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);

  const visibleAttachments = useMemo<RenoEmailAttachment[]>(() => {
    if (!selectedEmail) return [];
    const opgeslagen = selectedEmail.attachments ?? [];
    if (opgeslagen.length > 0) return opgeslagen;
    return fetchedAttachments[selectedEmail.messageId ?? ''] ?? [];
  }, [selectedEmail, fetchedAttachments]);

  useEffect(() => {
    const messageId = selectedEmail?.messageId;
    if (!session || !messageId) return;
    if (!selectedEmail?.hasAttachments) return;
    if ((selectedEmail.attachments ?? []).length > 0) return;
    if (fetchedAttachments[messageId]) return;

    let actief = true;
    setIsLoadingAttachments(true);
    fetchWithAuth(`${API_URL}/email/message-details/reno/${messageId}`, { method: 'GET' }, session)
      .then(async (res) => {
        if (!res.ok) throw new Error(`Bijlagen ophalen mislukt: ${res.status}`);
        const data = await res.json();
        if (!actief) return;
        setFetchedAttachments((prev) => ({
          ...prev,
          [messageId]: (data.attachments ?? [])
            .filter((att: any) => !att.isInline)
            .map((att: any) => ({
              name: att.name,
              size: att.size ?? 0,
              contentType: att.contentType ?? '',
              sharePointFileId: att.id ?? '',
            })),
        }));
      })
      .catch((error) => {
        // Een verzonden mail heeft geen opvraagbare Graph-id: dan blijft de lijst leeg.
        Sentry.captureException(error, {
          tags: { module: 'reno-helpdesk', action: 'fetchEmailAttachments' },
          extra: { messageId },
        });
        if (actief) setFetchedAttachments((prev) => ({ ...prev, [messageId]: [] }));
      })
      .finally(() => { if (actief) setIsLoadingAttachments(false); });

    return () => { actief = false; };
  }, [selectedEmail, session, fetchedAttachments]);

  // De geopende mail sluiten zodra ze niet meer bij het zichtbare kanaal hoort,
  // anders blijft een leveranciersmail rechts openstaan in het huurdergedeelte.
  useEffect(() => {
    if (selectedEmail && !renoEmailMatchesChannel(selectedEmail, channel)) {
      setSelectedEmail(null);
    }
  }, [selectedEmail, channel]);

  // Ontvangers hangen af van het kanaal:
  //  - Huurder: hele gebouw / huurder van de unit / contactpersoon
  //  - Leverancier: gekoppelde leveranciers / contactpersoon
  const recipientOptions = useMemo<RecipientOption[]>(() => {
    const opts: RecipientOption[] = [];
    if (channel === 'tenant' && recipientsData) {
      // Enkel huurders met een adres kunnen gemaild worden; de rest tellen we apart
      // zodat zichtbaar is dat er iemand ontbreekt.
      const mailable = (list: { name: string; email: string }[]) =>
        list.filter((t) => t.email?.trim()).map((t) => ({ name: t.name, email: t.email.trim() }));
      const buildingMailable = mailable(recipientsData.buildingTenants);
      const unitMailable = mailable(recipientsData.unitTenants);
      const buildingZonder = recipientsData.buildingTenants.length - buildingMailable.length;
      const unitZonder = recipientsData.unitTenants.length - unitMailable.length;

      opts.push({
        key: 'building',
        label: 'Hele gebouw',
        sublabel: `${buildingMailable.length} huurder(s) met e-mailadres${buildingZonder > 0 ? ` — ${buildingZonder} zonder` : ''}`,
        icon: 'building',
        recipients: buildingMailable,
        warning: buildingMailable.length === 0 ? 'Geen enkele huurder heeft een e-mailadres' : undefined,
      });
      opts.push({
        key: 'unit',
        label: 'Huurder van de unit',
        sublabel: unitMailable.map((t) => t.name).join(', ') || 'geen huurder gevonden',
        icon: 'unit',
        recipients: unitMailable,
        warning:
          unitMailable.length === 0
            ? unitZonder > 0
              ? 'Huurder van de unit heeft geen e-mailadres'
              : 'Geen huurder gevonden voor deze unit'
            : undefined,
      });
    }
    if (channel === 'supplier') {
      // Leveranciers komen LIVE uit het ticket (assignments) zodat toegevoegde/verwijderde
      // leveranciers meteen als ontvanger beschikbaar zijn, zonder aparte refetch.
      (ticket?.supplierAssignments ?? []).forEach((a) => {
        // Is er een contactpersoon gekozen, dan is díé de bestemming; anders het
        // algemene adres van de firma. Zelfde regel als in de backend.
        const contactNaam = a.contact
          ? `${a.contact.firstName ?? ''} ${a.contact.lastName ?? ''}`.trim()
          : '';
        const email = resolveSupplierRecipientEmail(a.contact, a.supplier?.email);
        const naam = contactNaam || a.supplier?.name || 'Leverancier';
        opts.push({
          key: `supplier-${a.supplierId}`,
          label: a.supplier?.name ?? 'Leverancier',
          sublabel: contactNaam
            ? `${contactNaam}${email ? ` — ${email}` : ''}`
            : email || 'Leverancier',
          icon: 'supplier',
          recipients: email ? [{ name: naam, email }] : [],
          warning: email
            ? undefined
            : contactNaam
              ? `${contactNaam} en de firma hebben geen e-mailadres`
              : 'Geen e-mailadres bij deze leverancier',
          // De fiche om een ontbrekend adres op aan te vullen: de contactpersoon
          // wanneer die gekozen is, anders de firma.
          entity: a.contact
            ? { type: 'contact', id: a.contact.id }
            : { type: 'company', id: a.supplierId },
        });
      });
    }
    // Contactpersoon is in beide kanalen beschikbaar.
    if (recipientsData?.contactPerson) {
      const contactEmail = recipientsData.contactPerson.email?.trim();
      opts.push({
        key: 'contact',
        label: 'Contactpersoon',
        sublabel: recipientsData.contactPerson.name,
        icon: 'contact',
        recipients: contactEmail
          ? [{ name: recipientsData.contactPerson.name, email: contactEmail }]
          : [],
        warning: contactEmail
          ? undefined
          : `${recipientsData.contactPerson.name} heeft geen e-mailadres`,
        entity: { type: 'contact', id: recipientsData.contactPerson.id },
      });
    }
    // Vrij toegevoegde ontvangers staan onderaan en blijven bij een kanaalwissel bestaan.
    extraRecipients.forEach((extra) => {
      opts.push({
        key: `extra-${extra.email.toLowerCase()}`,
        label: extra.name,
        sublabel:
          extra.type === 'company'
            ? `Firma — ${extra.email}`
            : extra.type === 'manual'
              ? 'Los e-mailadres'
              : `Contactpersoon — ${extra.email}`,
        icon: extra.type === 'company' ? 'supplier' : 'contact',
        recipients: [{ name: extra.name, email: extra.email }],
      });
    });
    return opts;
  }, [recipientsData, ticket?.supplierAssignments, channel, extraRecipients]);

  // Een toegevoegde ontvanger is meteen aangevinkt — anders lijkt "toevoegen" niets te doen.
  const handleAddExtraRecipient = useCallback((recipient: AddedRecipient) => {
    const email = recipient.email.trim();
    if (!email) return;
    const key = `extra-${email.toLowerCase()}`;
    setExtraRecipients((prev) =>
      prev.some((r) => r.email.toLowerCase() === email.toLowerCase())
        ? prev
        : [...prev, { ...recipient, email }],
    );
    setSelectedRecipientKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
    toast.success(`${recipient.name} toegevoegd als ontvanger`);
  }, []);

  /**
   * Adres net aangevuld bij een leverancier of contactpersoon. De ontvanger wordt
   * aangevinkt, het adres lokaal in het ticket bijgewerkt (geen volledige refetch:
   * dat zou de pagina in laadstatus zetten terwijl de kiezer openstaat) en de
   * ontvangerslijst stil hernieuwd.
   */
  const handleRecipientEmailAdded = useCallback(
    (option: RecipientOption, email: string) => {
      setSelectedRecipientKeys((prev) => (prev.includes(option.key) ? prev : [...prev, option.key]));

      if (option.entity?.type === 'company') {
        const supplierId = option.entity.id;
        setTicket((prev) =>
          prev
            ? {
                ...prev,
                supplierAssignments: (prev.supplierAssignments ?? []).map((a) =>
                  a.supplierId === supplierId && a.supplier
                    ? { ...a, supplier: { ...a.supplier, email } }
                    : a,
                ),
              }
            : prev,
        );
      }

      fetchRecipients();
    },
    [fetchRecipients],
  );

  // Geselecteerde ontvangers omzetten naar unieke {name,email}-lijst.
  const selectedRecipients = useMemo(() => {
    const map = new Map<string, { name: string; email: string }>();
    for (const opt of recipientOptions) {
      if (!selectedRecipientKeys.includes(opt.key)) continue;
      for (const r of opt.recipients) {
        if (r.email && !map.has(r.email)) map.set(r.email, r);
      }
    }
    return Array.from(map.values());
  }, [recipientOptions, selectedRecipientKeys]);

  // Openstand van de contactpersoon: wat er in de boekhouding nog openstaat. Komt van de
  // incassodossiers, dus geen extra Odoo-call per ticket.
  const { balances } = useTenantBalances([ticket?.reporter?.id]);

  // Reden waarom er (nog) niet gemaild kan worden; null = het kan.
  const mailBlockReason = useMemo(
    () =>
      renoMailBlockReason({
        channel,
        // Een bestaand ticket heeft altijd een gebouw of project als anker; het
        // gebouwloze geval bestaat enkel in de aanmaakflow.
        hasBuilding: true,
        options: recipientOptions,
        selectedCount: selectedRecipients.length,
      }),
    [channel, recipientOptions, selectedRecipients.length],
  );

  // Namen van mogelijke ontvangers zonder e-mailadres. Zonder deze melding lijkt het
  // alsof mailen gewoon niet werkt, terwijl het adres simpelweg ontbreekt.
  const recipientsWithoutEmail = useMemo(() => {
    const names = new Set<string>();
    if (channel === 'supplier') {
      (ticket?.supplierAssignments ?? []).forEach((a) => {
        // Er is een probleem zodra er nergens een adres is: niet bij de gekozen
        // contactpersoon en niet bij de firma. Heeft de contactpersoon er geen maar
        // de firma wel, dan valt de mail terug op de firma en is er niets aan de hand.
        if (a.supplier && !resolveSupplierRecipientEmail(a.contact, a.supplier.email)) {
          names.add(a.supplier.name || 'Leverancier');
        }
      });
      const legacy = ticket?.supplier as { id: number; name?: string; email?: string } | undefined;
      if (legacy && !(ticket?.supplierAssignments ?? []).length && !legacy.email?.trim()) {
        names.add(legacy.name || 'Leverancier');
      }
    }
    if (recipientsData?.contactPerson && !recipientsData.contactPerson.email?.trim()) {
      names.add(recipientsData.contactPerson.name);
    }
    return Array.from(names);
  }, [channel, ticket?.supplierAssignments, ticket?.supplier, recipientsData]);

  const handleUpdate = async (updates: Partial<RenoTicket>) => {
    if (!session || !id) return;
    setIsSaving(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/reno-helpdesk/tickets/${id}`,
        { method: 'PATCH', body: JSON.stringify(updates), headers: { 'Content-Type': 'application/json' } },
        session,
      );
      if (!res.ok) throw new Error('Update mislukt');
      setTicket(await res.json());
      toast.success('Opgeslagen');
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddSupplier = async (supplierId: number) => {
    if (!session || !id) return;
    try {
      const res = await fetchWithAuth(
        `${API_URL}/reno-helpdesk/tickets/${id}/suppliers`,
        { method: 'POST', body: JSON.stringify({ supplierId }), headers: { 'Content-Type': 'application/json' } },
        session,
      );
      if (!res.ok) throw new Error('Leverancier toevoegen mislukt');
      await fetchTicket();
      toast.success('Leverancier toegevoegd');
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleRemoveSupplier = async (assignmentId: number) => {
    if (!session || !id) return;
    try {
      const res = await fetchWithAuth(
        `${API_URL}/reno-helpdesk/tickets/${id}/suppliers/${assignmentId}`,
        { method: 'DELETE' },
        session,
      );
      if (!res.ok) throw new Error('Leverancier verwijderen mislukt');
      await fetchTicket();
      toast.success('Leverancier verwijderd');
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  /**
   * Kiest de contactpersoon bij één leverancier van dit ticket. `null` wist de
   * keuze, waarna de mail weer naar het algemene adres van de firma gaat.
   */
  const handleSetSupplierContact = async (
    assignmentId: number,
    contact: CompanyContactPerson | null,
  ) => {
    if (!session || !id) return;
    try {
      const res = await fetchWithAuth(
        `${API_URL}/reno-helpdesk/tickets/${id}/suppliers/${assignmentId}/contact`,
        {
          method: 'PUT',
          body: JSON.stringify({ contactId: contact?.id ?? null }),
          headers: { 'Content-Type': 'application/json' },
        },
        session,
      );
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || 'Contactpersoon instellen mislukt');
      }
      await fetchTicket();
      toast.success(
        contact
          ? `Communicatie loopt nu via ${contact.name}`
          : 'Communicatie loopt nu via het algemene adres van de firma',
      );
    } catch (err: any) {
      Sentry.captureException(err, {
        tags: { module: 'reno-helpdesk', action: 'setSupplierContact' },
        extra: { ticketId: id, assignmentId, contactId: contact?.id ?? null },
      });
      toast.error(err.message);
    }
  };

  const handleTitleSave = async () => {
    if (!editedTitle.trim()) return;
    await handleUpdate({ title: editedTitle.trim() });
    setIsEditingTitle(false);
  };

  const handleReopen = async (reason: string) => {
    if (!session || !id) return;
    const res = await fetchWithAuth(
      `${API_URL}/reno-helpdesk/tickets/${id}/reopen`,
      { method: 'PATCH', body: JSON.stringify({ reason }), headers: { 'Content-Type': 'application/json' } },
      session,
    );
    if (!res.ok) throw new Error('Heropenen mislukt');
    setTicket(await res.json());
    toast.success('Ticket heropend');
  };

  const handleDueDateChange = (value: string) => {
    handleUpdate({ dueDate: value ? new Date(value).toISOString() : null } as any);
  };

  const handleInterventionDateChange = (value: string) => {
    handleUpdate({ interventionDate: value ? new Date(value).toISOString() : null } as any);
  };

  const handleAssignAndNotify = async (assignedTo: string, message: string) => {
    if (!session || !id) return;
    const res = await fetchWithAuth(
      `${API_URL}/reno-helpdesk/tickets/${id}/assign`,
      { method: 'POST', body: JSON.stringify({ assignedTo, message }), headers: { 'Content-Type': 'application/json' } },
      session,
    );
    if (!res.ok) throw new Error('Doorsturen mislukt');
    setTicket(await res.json());
    toast.success('Ticket doorgestuurd');
    setIsAssignOpen(false);
  };

  const handleRenoSend = async (emailData: any) => {
    if (!id) throw new Error('Geen ticket beschikbaar');
    const result = await sendRenoTicketEmail(
      Number(id),
      emailData,
      channel,
      session,
      mailMessageType,
    );

    // Rapport klaarzetten: de composer sluit meteen, dit venster toont naar wie
    // er gemaild is en of het gelukt is.
    setSendReport(
      buildRenoSendReport(result, emailData.contacts ?? [], emailData.subject ?? ''),
    );

    await fetchTicket();
    return result;
  };

  const handleDelete = async () => {
    if (!session || !id) return;
    try {
      const res = await fetchWithAuth(
        `${API_URL}/reno-helpdesk/tickets/${id}`,
        { method: 'DELETE' },
        session,
      );
      if (!res.ok) throw new Error('Verwijderen mislukt');
      toast.success('Ticket verwijderd');
      navigate('/renotickets');
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-gray-500">Ticket laden...</p>
      </div>
    );
  }

  if (!ticket) return null;

  const isClosed = ticket.status === RenoTicketStatus.AFGEROND;
  const gebouwId = ticket.building?.id ?? ticket.renovationProject?.building?.id ?? null;
  const renoCode = getRenoCommunicationCode(ticket.id, channel);
  const assignedUser = ticket.assignedTo ? getUserName(ticket.assignedTo, users) : null;
  const assignedImageUrl = ticket.assignedTo ? getUserImageUrlById(ticket.assignedTo, users) : null;

  return (
    <div className="min-h-screen">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-muted border-b border-gray-200 py-3 px-6 shadow-sm">
        <div className="flex items-start justify-between">
          <div className="flex flex-1 items-start gap-4">
            <button
              onClick={() => navigate('/renotickets')}
              className="mt-1 text-gray-500 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex-1">
              <div className="mb-2 flex items-center gap-3">
                <div className="h-8 w-3 rounded-full bg-amber-500" />
                <span className="text-2xl font-bold text-gray-900" title="Communicatiecode (RS = leverancier, RT = huurder)">{renoCode}</span>
                {isEditingTitle ? (
                  <div className="flex flex-1 items-center gap-2">
                    <input
                      type="text"
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleTitleSave(); if (e.key === 'Escape') setIsEditingTitle(false); }}
                      autoFocus
                      className="flex-1 rounded border border-gray-300 px-3 py-1 text-lg font-semibold focus:outline-none focus:border-gray-500"
                    />
                    <Button size="sm" onClick={handleTitleSave}>Opslaan</Button>
                    <Button size="sm" variant="outline" onClick={() => setIsEditingTitle(false)}>Annuleren</Button>
                  </div>
                ) : (
                  <div className="group flex flex-1 items-center gap-2">
                    <h1 className="text-lg font-semibold text-gray-900">{ticket.title}</h1>
                    <button
                      onClick={() => { setEditedTitle(ticket.title); setIsEditingTitle(true); }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-gray-700"
                    >
                      <PencilSimple size={14} />
                    </button>
                  </div>
                )}
              </div>

              {/* Chips */}
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[ticket.status]}`}>
                  <span className="h-1.5 w-1.5 rounded-full bg-current" />
                  {ticket.status}
                </span>
                {ticket.priority && ticket.priority !== 'Unknown' && (
                  <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                    <Star size={10} />
                    {ticket.priority}
                  </span>
                )}
                {gebouwId && (
                  <span className="inline-flex items-center gap-1 rounded border border-gray-300 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-700">
                    <Buildings size={10} weight="duotone" />
                    {gebouwId}
                  </span>
                )}
                {ticket.unit?.id && (
                  <span className="inline-flex items-center gap-1 rounded border border-gray-300 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-700">
                    <Cube size={10} weight="duotone" />
                    {ticket.unit.id}
                  </span>
                )}
                {ticket.renovationProject && (
                  <button
                    onClick={() => navigate(`/renovation/${ticket.renovationProject!.id}`)}
                    className="inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 hover:bg-amber-200"
                    title={`Renovatieprojectcode: RENO-${ticket.renovationProject.id}`}
                  >
                    <Wall size={10} weight="duotone" />
                    RENO-{ticket.renovationProject.id} · {ticket.renovationProject.title}
                  </button>
                )}
                {ticket.projectPart?.label?.name && (
                  <span className="inline-flex items-center gap-1 rounded border border-moss-primary/30 bg-moss-secondary-fine px-2 py-0.5 text-xs font-medium text-moss-primary">
                    <Tag size={10} weight="duotone" />
                    {ticket.projectPart.label.name}
                  </span>
                )}
                {ticket.messageType && (
                  <span className="inline-flex items-center gap-1 rounded border border-indigo-300 bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">
                    {ticket.messageType}
                  </span>
                )}
                {(ticket.supplierAssignments ?? []).map((a) => (
                  <span key={a.id} className="inline-flex items-center gap-1 rounded border border-gray-300 bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                    <WrenchIcon size={10} />
                    {a.supplier?.name}
                  </span>
                ))}
                {ticket.dueDate && (
                  <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium ${
                    new Date(ticket.dueDate) < new Date() ? 'border-red-300 bg-red-100 text-red-700' : 'border-orange-300 bg-orange-100 text-orange-700'
                  }`}>
                    <Calendar size={10} />
                    Deadline: {formatDate(ticket.dueDate)}
                  </span>
                )}
                {ticket.createdAt && (
                  <span className="inline-flex items-center gap-1 rounded border border-gray-300 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600">
                    <Calendar size={10} />
                    {formatDate(ticket.createdAt)}
                  </span>
                )}
                {isClosed && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsReopenModalOpen(true)}
                    className="h-6 px-2 text-xs gap-1"
                  >
                    <ArrowCounterClockwise size={12} />
                    Heropenen
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setIsAssignOpen(true)}
                  className="h-6 px-2 text-xs gap-1"
                >
                  <ArrowsOut size={12} />
                  Doorsturen
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setIsMergeModalOpen(true)}
                  className="h-6 px-2 text-xs gap-1"
                >
                  <GitMerge size={12} />
                  Samenvoegen
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setIsDeleteOpen(true)}
                  className="h-6 px-2 text-xs gap-1 text-red-600 hover:text-red-700 hover:border-red-300"
                >
                  <Trash size={12} />
                  Verwijderen
                </Button>
              </div>
            </div>
          </div>

          {/* Huurder / Leverancier toggle (rechts, zoals helpdesk): kanaal + code (RS/RT) */}
          <div className="ml-4 flex shrink-0 items-center gap-3 rounded-lg border border-neutral-primary/20 bg-white/80 px-5 py-2.5">
            <span className={`text-base ${channel === 'tenant' ? 'font-semibold text-moss-primary' : 'text-neutral-secondary-mid'}`}>
              Huurder
            </span>
            <Switch
              checked={channel === 'supplier'}
              onCheckedChange={(c) => setChannel(c ? 'supplier' : 'tenant')}
              className="scale-125"
            />
            <span className={`text-base ${channel === 'supplier' ? 'font-semibold text-moss-primary' : 'text-neutral-secondary-mid'}`}>
              Leverancier
            </span>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="mx-auto max-w-full px-6 pb-6 space-y-6 pt-6">

        {/* Financiële documenten uit Renovatie (offertes, facturen, bestelbonnen).
            Hieruit komen het goedgekeurde offertebedrag en het factuurbedrag. */}
        <RenoFinancialDocumentsSection
          ticketId={ticket.id}
          approvedQuoteAmount={ticket.approvedQuoteAmount}
          invoiceAmount={ticket.invoiceAmount}
          onChanged={fetchTicket}
        />

        {/* Documenten & facturen */}
        <RenoTicketDocumentsSection ticketId={ticket.id} />

        {/* Foto's */}
        <RenoTicketPhotosSection ticketId={ticket.id} />

        {/* E-mailverloop + details: twee kaarten naast elkaar, zoals de helpdesk.
            Gestapeld onder xl, want de tabel heeft breedte nodig voor Van/Naar/Onderwerp. */}
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="bg-muted overflow-hidden rounded-lg border border-muted-foreground/20">
          <div className="border-b border-neutral-primary/10 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <EnvelopeSimple size={20} className="text-moss-primary" />
              <h3 className="font-semibold text-neutral-primary">
                E-mailverloop {channel === 'tenant' ? 'huurder' : 'leverancier'}
                {channelEmails.length > 0 && <span className="ml-1">({channelEmails.length})</span>}
              </h3>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <RenoRecipientSelect
                options={recipientOptions}
                selectedKeys={selectedRecipientKeys}
                onChange={setSelectedRecipientKeys}
                onAddRecipient={handleAddExtraRecipient}
                onRecipientEmailAdded={handleRecipientEmailAdded}
                open={isRecipientSelectOpen}
                onOpenChange={setIsRecipientSelectOpen}
              />
              {/* Waarover de mail gaat: per mail bewaard, voorgevuld met het tickettype. */}
              <RenoMessageTypeSelect value={mailMessageType} onChange={setMailMessageType} />
              {recipientsWithoutEmail.length > 0 ? (
                // Klikbaar: het paneel met de ontvangers bevat het veld om het adres
                // aan te vullen. Als passieve tekst wist niemand dat dat daar kon.
                <button
                  type="button"
                  onClick={() => setIsRecipientSelectOpen(true)}
                  className="flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-900 hover:bg-amber-100"
                  title="Klik om het e-mailadres hier aan te vullen"
                >
                  <Warning size={12} weight="fill" className="text-amber-700" />
                  Geen e-mailadres: {recipientsWithoutEmail.join(', ')} — aanvullen
                </button>
              ) : (
                selectedRecipients.length === 0 && (
                  <span className="text-xs italic text-neutral-secondary-mid">
                    Nog geen ontvanger gekozen
                  </span>
                )
              )}
              <Button
                size="sm"
                onClick={() => setIsSendEmailOpen(true)}
                title={mailBlockReason ?? 'E-mail opstellen'}
                className="h-7 gap-1 bg-moss-primary px-2 text-xs text-white hover:bg-moss-primary/90"
              >
                <EnvelopeSimple size={12} />
                E-mail sturen
              </Button>
            </div>
          </div>

          {/* Waarom er (nog) niet gemaild kan worden + de regels erachter. */}
          <div className="border-b border-neutral-primary/10 px-4 py-2">
            <RenoMailInfoNotice
              blockReason={mailBlockReason}
              actionLabel={recipientsWithoutEmail.length > 0 ? 'Ontvangers openen' : undefined}
              onAction={() => setIsRecipientSelectOpen(true)}
            />
          </div>

          <div className="overflow-auto" style={{ minHeight: 220, maxHeight: 520 }}>
              {channelEmails.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 p-8 py-12">
                  <EnvelopeSimple size={48} className="text-neutral-secondary-mid/50" />
                  <p className="text-sm text-neutral-secondary-mid">
                    {channel === 'tenant'
                      ? 'Nog geen e-mails met de huurder'
                      : 'Nog geen e-mails met de leverancier'}
                  </p>
                </div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="sticky top-0 z-10">
                    <tr className="border-b border-muted-foreground/20 bg-muted-foreground/10 text-foreground uppercase tracking-wide">
                      <th className="px-3 py-2 text-left font-medium">Richting</th>
                      <th className="px-3 py-2 text-left font-medium">Datum</th>
                      <th className="px-3 py-2 text-left font-medium">Van</th>
                      <th className="px-3 py-2 text-left font-medium">Naar</th>
                      <th className="px-3 py-2 text-left font-medium">Onderwerp</th>
                      <th className="px-3 py-2 text-left font-medium">Bijlagen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {channelEmails
                      .map((email: any) => {
                        const isSent = email.direction === 'sent';
                        const isSelected = selectedEmail?.id === email.id;
                        // Bij een verzonden mail is `from` het reno-postvak; de ontvanger
                        // staat in de metadata. Zie utils/renoEmailParticipants.ts.
                        const { from, to, extraCount, tooltip } = describeRenoEmailFromTo(
                          email,
                          resolveRecipientName,
                        );
                        return (
                          <tr
                            key={email.id}
                            onClick={() => setSelectedEmail(email as RenoEmailItem)}
                            className={`cursor-pointer border-b border-neutral-primary/10 transition-colors ${
                              isSelected
                                ? 'border-l-4 border-l-moss-primary bg-moss-primary/10'
                                : 'hover:bg-neutral-primary/5'
                            }`}
                          >
                            <td className="px-3 py-2.5 shrink-0">
                              <div className="flex items-center gap-1.5">
                                {isSent
                                  ? <ArrowRight size={14} className="text-moss-primary shrink-0" />
                                  : <ArrowLeft size={14} className="text-blue-600 shrink-0" />
                                }
                                <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${
                                  isSent ? 'bg-moss-primary/10 text-moss-primary' : 'bg-blue-100 text-blue-700'
                                }`}>
                                  {isSent ? 'Verzonden' : 'Ontvangen'}
                                </span>
                              </div>
                            </td>
                            <td className="px-3 py-2.5 whitespace-nowrap">
                              <div className="font-medium text-neutral-primary">{new Date(email.receivedDateTime).toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit', year: '2-digit' })}</div>
                              <div className="text-neutral-secondary-mid">{new Date(email.receivedDateTime).toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })}</div>
                            </td>
                            <td className="max-w-32 px-3 py-2.5">
                              <div className="truncate font-medium text-neutral-primary" title={from}>
                                {from}
                              </div>
                            </td>
                            <td className="max-w-32 px-3 py-2.5">
                              <div className="flex items-center gap-1 min-w-0" title={tooltip}>
                                <span className="truncate font-medium text-neutral-primary">{to}</span>
                                {extraCount > 0 && (
                                  <span className="shrink-0 rounded-full bg-muted-foreground/10 px-1.5 text-[10px] font-medium text-neutral-secondary-mid">
                                    +{extraCount}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="max-w-48 px-3 py-2.5">
                              <div className="flex items-center gap-1 min-w-0">
                                {/* Type van de mail (Offerte, Factuur, …): per mail bewaard bij
                                    het verzenden, antwoorden erven het van de mail waarop
                                    geantwoord werd. Geen type ⇒ geen chip. */}
                                {email.metadata?.messageType && (
                                  <span className="shrink-0 rounded-full bg-moss-secondary-light px-1.5 text-[10px] font-medium text-moss-primary">
                                    {email.metadata.messageType}
                                  </span>
                                )}
                                <span className="truncate font-medium text-neutral-primary" title={email.subject}>
                                  {email.subject}
                                </span>
                              </div>
                            </td>
                            <td className="px-3 py-2.5">
                              {email.hasAttachments && (
                                <div className="flex items-center gap-1 text-neutral-secondary-mid">
                                  <Paperclip size={14} />
                                  <span>{email.attachments?.length ?? ''}</span>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              )}
          </div>
        </div>

        {/* Tweede kaart: de geopende mail. */}
        <div className="bg-muted overflow-hidden rounded-lg border border-muted-foreground/20">
            <div className="min-w-0 overflow-y-auto" style={{ maxHeight: 578 }}>
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-neutral-primary/10 bg-muted px-4 py-3">
                <div className="flex items-center gap-2">
                  <EnvelopeSimple size={20} className="text-moss-primary" />
                  <h3 className="font-semibold text-neutral-primary">E-mail details</h3>
                </div>
                {selectedEmail && (
                  <button
                    onClick={() => setSelectedEmail(null)}
                    className="shrink-0 text-neutral-secondary-mid hover:text-moss-primary"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
              {selectedEmail ? (
                <div className="p-6">
                  <div className="mb-4 pb-4 border-b border-neutral-primary/10">
                    <h4 className="mb-2 text-lg font-semibold text-neutral-primary">{selectedEmail.subject}</h4>
                    <div className="space-y-1 text-sm text-neutral-secondary-mid">
                      <div><strong>Van:</strong> {selectedEmail.from}</div>
                      {selectedEmail.metadata?.to && (
                        <div><strong>Naar:</strong> {selectedEmail.metadata.to}</div>
                      )}
                      {/* CC/BCC stonden enkel in de databank: zonder deze regels was niet
                          te zien dat er nog iemand meegelezen heeft. */}
                      {(selectedEmail.metadata?.cc ?? []).length > 0 && (
                        <div><strong>CC:</strong> {selectedEmail.metadata!.cc!.join(', ')}</div>
                      )}
                      {(selectedEmail.metadata?.bcc ?? []).length > 0 && (
                        <div><strong>BCC:</strong> {selectedEmail.metadata!.bcc!.join(', ')}</div>
                      )}
                      <div><strong>Datum:</strong> {new Date(selectedEmail.receivedDateTime).toLocaleString('nl-BE')}</div>
                      <div className="flex items-center gap-2 pt-1">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                          selectedEmail.direction === 'sent' ? 'bg-moss-primary/10 text-moss-primary' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {selectedEmail.direction === 'sent' ? 'Verzonden' : 'Ontvangen'}
                        </span>
                        {selectedEmail.metadata?.messageType && (
                          <span className="inline-flex rounded-full bg-moss-secondary-light px-2 py-0.5 text-xs font-medium text-moss-primary">
                            {selectedEmail.metadata.messageType}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {selectedEmail.body?.trim().startsWith('<') ? (
                    <TranslatePreview
                      htmlContent={selectedEmail.body}
                      bodyClassName="prose prose-sm max-w-none text-neutral-primary bg-white p-4 rounded-sm border max-h-96 overflow-y-auto"
                    />
                  ) : (
                    <pre className="max-h-96 overflow-y-auto whitespace-pre-wrap rounded-sm border bg-white p-4 font-sans text-sm text-neutral-primary">{selectedEmail.body}</pre>
                  )}

                  {/* Bijlagen zoals in het helpdesk-verloop: foto's inline met lightbox,
                      PDF's in een nieuw tabblad. De inhoud komt uit het postvak
                      (source="reno"), want reno-mails hebben geen SharePoint-kopie. */}
                  {(visibleAttachments.length > 0 || isLoadingAttachments) && (
                    <div className="mt-6 space-y-2">
                      <h5 className="mb-2 text-sm font-medium text-neutral-primary">
                        Bijlagen{visibleAttachments.length > 0 ? ` (${visibleAttachments.length})` : ''}
                      </h5>
                      {isLoadingAttachments && visibleAttachments.length === 0 ? (
                        <p className="text-xs text-neutral-secondary-mid">Bijlagen laden...</p>
                      ) : (
                        visibleAttachments.map((att, i) => (
                          <AttachmentViewer
                            key={`${att.name}-${i}`}
                            attachment={att}
                            email={selectedEmail as any}
                            source="reno"
                          />
                        ))
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 p-8 py-12">
                  <EnvelopeSimple size={48} className="text-neutral-secondary-mid/50" />
                  <p className="text-sm text-neutral-secondary-mid text-center">Klik op een e-mail om te openen</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Details + Action Notes */}
        <div className="bg-muted overflow-hidden rounded-lg border border-muted-foreground/20">
          <div className="border-b border-neutral-primary/10 px-4 py-3">
            <div className="flex items-center gap-2">
              {/* Zelfde kop als de helpdesk-ticketdetailpagina (moss, size 18). */}
              <PencilSimple size={18} className="text-moss-primary" weight="duotone" />
              <h3 className="text-base font-semibold text-neutral-primary">Ticket Details</h3>
            </div>
          </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
              {/* Detail fields */}
              <div className="divide-y divide-gray-200">

                <div className="px-4 py-3 space-y-1">
                  <Label className="text-xs text-gray-500">Status</Label>
                  <Select
                    value={ticket.status}
                    onValueChange={(v) => handleUpdate({ status: v as RenoTicketStatus })}
                    disabled={isSaving}
                  >
                    <SelectTrigger className="text-xs h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(RenoTicketStatus).filter((s) => s !== RenoTicketStatus.UNKNOWN).map((s) => (
                        <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="px-4 py-3 space-y-1">
                  <Label className="text-xs text-gray-500">Prioriteit</Label>
                  <Select
                    value={ticket.priority}
                    onValueChange={(v) => handleUpdate({ priority: v as RenoTicketPriority })}
                    disabled={isSaving}
                  >
                    <SelectTrigger className="text-xs h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(RenoTicketPriority).map((p) => (
                        <SelectItem key={p} value={p} className="text-xs">{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="px-4 py-3 space-y-1">
                  <Label className="text-xs text-gray-500">Deadline</Label>
                  <input
                    type="date"
                    defaultValue={formatDateInput(ticket.dueDate)}
                    onChange={(e) => handleDueDateChange(e.target.value)}
                    className="h-8 w-full rounded-md border border-gray-200 bg-white px-3 text-xs outline-none focus:border-gray-400"
                  />
                </div>

                {/* Interventiedatum: wanneer er iemand ter plaatse komt. Handmatig,
                    zoals "Interventie gepland" bij de helpdesk — bij renovatie wordt
                    dat per mail afgesproken, niet via een agenda-integratie. */}
                <div className="px-4 py-3 space-y-1">
                  <Label className="text-xs text-gray-500">Interventiedatum</Label>
                  <input
                    type="date"
                    defaultValue={formatDateInput(ticket.interventionDate ?? undefined)}
                    onChange={(e) => handleInterventionDateChange(e.target.value)}
                    className="h-8 w-full rounded-md border border-gray-200 bg-white px-3 text-xs outline-none focus:border-gray-400"
                  />
                </div>

                <div className="px-4 py-3 space-y-1">
                  <Label className="text-xs text-gray-500">Renovatieproject</Label>
                  <button
                    onClick={() => setPickerOpen('project')}
                    className="w-full text-left h-8 rounded-md border border-gray-200 bg-white px-3 text-xs hover:border-gray-400 transition-colors flex items-center justify-between"
                  >
                    <span className={ticket.renovationProject ? 'text-gray-900' : 'text-gray-400'}>
                      {ticket.renovationProject?.title ?? 'Selecteer project...'}
                    </span>
                    <PencilSimple size={12} className="text-gray-400" />
                  </button>
                </div>

                <div className="px-4 py-3 space-y-1">
                  <Label className="text-xs text-gray-500">Interne verantwoordelijke</Label>
                  <button
                    onClick={() => setPickerOpen('assignee')}
                    className="w-full text-left h-8 rounded-md border border-gray-200 bg-white px-3 text-xs hover:border-gray-400 transition-colors flex items-center gap-2"
                  >
                    {assignedImageUrl ? (
                      <img src={assignedImageUrl} className="h-5 w-5 rounded-full" alt="" />
                    ) : (
                      <User size={14} className="text-gray-400 shrink-0" />
                    )}
                    <span className={assignedUser?.name ? 'text-gray-900' : 'text-gray-400'}>
                      {assignedUser?.name || 'Niet toegewezen'}
                    </span>
                    <PencilSimple size={12} className="text-gray-400 ml-auto" />
                  </button>
                </div>

                <div className="px-4 py-3 space-y-1">
                  <Label className="text-xs text-gray-500">Contactpersoon</Label>
                {/* Openstand staat náást de naam, maar buiten de knop: het badge is zelf
                    een link naar het incassodossier, en een link in een knop is ongeldige
                    HTML die in browsers verschillend afloopt. */}
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setPickerOpen('reporter')}
                    className="min-w-0 flex-1 text-left h-8 rounded-md border border-gray-200 bg-white px-3 text-xs hover:border-gray-400 transition-colors flex items-center justify-between gap-2"
                  >
                    <span className={`truncate ${ticket.reporter ? 'text-gray-900' : 'text-gray-400'}`}>
                      {ticket.reporter
                        ? `${ticket.reporter.firstName} ${ticket.reporter.lastName}`.trim()
                        : 'Selecteer contact...'}
                    </span>
                    <PencilSimple size={12} className="text-gray-400 shrink-0" />
                  </button>
                  <TenantBalanceBadge
                    name={
                      ticket.reporter
                        ? `${ticket.reporter.firstName} ${ticket.reporter.lastName}`.trim()
                        : undefined
                    }
                    balance={
                      balances[tenantBalanceKey('contact', ticket.reporter?.id) ?? ''] ??
                      undefined
                    }
                  />
                </div>
                </div>

                <RenoTagsInput
                  tags={ticket.tags ?? []}
                  onChange={(tags) => handleUpdate({ tags } as any)}
                  disabled={isSaving}
                />

                <div className="px-4 py-3 space-y-1">
                  <Label className="text-xs text-gray-500">Projectonderdeel</Label>
                  <button
                    onClick={() => setPickerOpen('part')}
                    className="w-full text-left h-8 rounded-md border border-gray-200 bg-white px-3 text-xs hover:border-gray-400 transition-colors flex items-center justify-between"
                  >
                    <span className={ticket.projectPart ? 'text-gray-900' : 'text-gray-400'}>
                      {ticket.projectPart?.title ?? 'Selecteer onderdeel...'}
                    </span>
                    <PencilSimple size={12} className="text-gray-400" />
                  </button>
                </div>

                <div className="px-4 py-3 space-y-1.5">
                  <Label className="text-xs text-gray-500">Leveranciers</Label>
                  <div className="flex flex-col items-start gap-1.5">
                    {(ticket.supplierAssignments ?? []).map((a) => (
                      <div key={a.id} className="flex flex-wrap items-center gap-1.5">
                        <span className="inline-flex items-center gap-1 rounded-md border border-neutral-primary/20 bg-white px-2 py-1 text-xs text-gray-900">
                          <WrenchIcon size={11} className="text-neutral-secondary-mid" />
                          {a.supplier?.name}
                          <button
                            onClick={() => handleRemoveSupplier(a.id)}
                            className="ml-0.5 text-gray-400 hover:text-red-600"
                            title="Leverancier verwijderen"
                          >
                            <X size={11} weight="bold" />
                          </button>
                        </span>

                        {/*
                          Naar wie er bij deze leverancier gemaild wordt. Zonder
                          keuze is dat het algemene adres van de firma.
                        */}
                        <CompanyContactPicker
                          companyId={a.supplier?.id}
                          companyName={a.supplier?.name}
                          companyEmail={a.supplier?.email}
                          value={toCompanyContactPerson(a.contact, a.supplier)}
                          onChange={(contact) => handleSetSupplierContact(a.id, contact)}
                          disabled={isSaving}
                          compact
                        />
                      </div>
                    ))}
                    <button
                      onClick={() => setPickerOpen('supplier')}
                      className="inline-flex items-center gap-1 rounded-md border border-dashed border-neutral-primary/30 px-2 py-1 text-xs text-neutral-secondary-mid hover:border-moss-primary hover:text-moss-primary transition-colors"
                    >
                      <Plus size={11} weight="bold" />
                      Leverancier toevoegen
                    </button>
                  </div>
                  {(ticket.supplierAssignments ?? []).length === 0 && (
                    <p className="text-xs text-gray-400">Nog geen leveranciers gekoppeld</p>
                  )}
                </div>

                <div className="px-4 py-3 space-y-1">
                  <Label className="text-xs text-gray-500">Bron</Label>
                  <Select
                    value={ticket.source}
                    onValueChange={(v) => handleUpdate({ source: v as RenoTicketSource })}
                    disabled={isSaving}
                  >
                    <SelectTrigger className="text-xs h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(RenoTicketSource).map((s) => (
                        <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="px-4 py-3 space-y-1">
                  <Label className="text-xs text-gray-500">Type boodschap</Label>
                  <Select
                    value={ticket.messageType ?? 'none'}
                    onValueChange={(v) => handleUpdate({ messageType: v === 'none' ? null : (v as RenoMessageType) } as any)}
                    disabled={isSaving}
                  >
                    <SelectTrigger className="text-xs h-8">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" className="text-xs">—</SelectItem>
                      {Object.values(RenoMessageType).map((m) => (
                        <SelectItem key={m} value={m} className="text-xs">{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="px-4 py-3 space-y-0.5 text-xs text-gray-600">
                  <div><span className="font-medium text-gray-900">Aangemaakt: </span>{formatDate(ticket.createdAt)}</div>
                  {ticket.completedAt && <div><span className="font-medium text-gray-900">Afgerond: </span>{formatDate(ticket.completedAt)}</div>}
                  {ticket.reopenedAt && <div><span className="font-medium text-gray-900">Heropend: </span>{formatDate(ticket.reopenedAt)}</div>}
                </div>

                <div className="px-4 py-3 space-y-1">
                  <Label className="text-xs text-gray-500">Beschrijving</Label>
                  <Textarea
                    defaultValue={ticket.description ?? ''}
                    onBlur={(e) => {
                      if (e.target.value !== (ticket.description ?? '')) {
                        handleUpdate({ description: e.target.value });
                      }
                    }}
                    placeholder="Beschrijving..."
                    rows={3}
                    className="text-xs"
                  />
                </div>

                <div className="px-4 py-3 space-y-1">
                  <Label className="text-xs text-gray-500">Interne notities</Label>
                  <Textarea
                    defaultValue={ticket.internalNotes ?? ''}
                    onBlur={(e) => {
                      if (e.target.value !== (ticket.internalNotes ?? '')) {
                        handleUpdate({ internalNotes: e.target.value });
                      }
                    }}
                    placeholder="Interne opmerkingen..."
                    rows={3}
                    className="text-xs"
                  />
                </div>
              </div>

              {/* Action Notes */}
              <div>
                <RenoActionNotesSection ticketId={ticket.id} />
              </div>
            </div>
          </div>

        {/* Linked tickets */}
        <div className="bg-muted overflow-hidden rounded-lg border border-muted-foreground/20">
          <div className="px-4 py-4">
            <RenoLinkedTicketsSection ticketId={ticket.id} />
          </div>
        </div>

        {/* Phase history */}
        <div className="bg-muted overflow-hidden rounded-lg border border-muted-foreground/20">
          <div className="border-b border-neutral-primary/10 px-4 py-3">
            <div className="flex items-center gap-2">
              <ActivityIcon size={18} className="text-purple-600" weight="duotone" />
              <h3 className="text-base font-semibold text-neutral-primary">Fasegeschiedenis</h3>
            </div>
          </div>
          <div className="px-4 py-4">
            <RenoPhaseHistoryTimeline ticketId={ticket.id} />
          </div>
        </div>
      </div>

      {/* Reopen modal */}
      {ticket && (
        <ReopenReasonModal
          isOpen={isReopenModalOpen}
          ticketId={ticket.id}
          onClose={() => setIsReopenModalOpen(false)}
          onConfirm={handleReopen}
        />
      )}

      {/* Send email modal */}
      {ticket && (
        <RenoMassMailModal
          isOpen={isSendEmailOpen}
          ticket={ticket}
          channel={channel}
          recipients={selectedRecipients}
          onClose={() => setIsSendEmailOpen(false)}
          onSuccess={() => { setIsSendEmailOpen(false); fetchTicket(); }}
          onSend={handleRenoSend}
          // Enkel het verloop van dit kanaal: bij een leveranciersmail hoort de
          // huurdercorrespondentie niet in het antwoordvenster.
          emailHistory={channelEmails as any}
        />
      )}

      {/* Verzendrapport: toont naar wie er gemaild is en of het gelukt is. */}
      <SendResultsModal
        isOpen={sendReport !== null}
        onClose={() => setSendReport(null)}
        results={sendReport}
      />

      {/* Merge modal */}
      {ticket && (
        <MergeRenoTicketModal
          isOpen={isMergeModalOpen}
          sourceTicket={ticket}
          onClose={() => setIsMergeModalOpen(false)}
          onMerged={(targetId) => {
            setIsMergeModalOpen(false);
            navigate(`/renotickets/${targetId}`);
          }}
        />
      )}

      {/* Renovation project picker */}
      <UniversalPickerModal
        isOpen={pickerOpen === 'project'}
        mode="renovation-project"
        title="Renovatieproject wijzigen"
        onClose={() => setPickerOpen(null)}
        onConfirm={(project: any) => {
          setPickerOpen(null);
          if (project) {
            handleUpdate({ renovationProjectId: project.id, projectPartId: null } as any);
          }
        }}
        config={{
          defaultValue: ticket.renovationProject ?? undefined,
          allowRemoval: false,
        }}
      />

      {/* Assignee picker */}
      <UniversalPickerModal
        isOpen={pickerOpen === 'assignee'}
        mode="assignee"
        title="Toewijzen aan medewerker"
        onClose={() => setPickerOpen(null)}
        onConfirm={(user: any) => {
          setPickerOpen(null);
          handleUpdate({ assignedTo: user?.id ?? null } as any);
        }}
        config={{
          defaultValue: ticket.assignedTo ?? undefined,
          allowRemoval: true,
        }}
      />

      {/* Reporter picker */}
      <UniversalPickerModal
        isOpen={pickerOpen === 'reporter'}
        mode="reporter"
        title="Verslaggever instellen"
        onClose={() => setPickerOpen(null)}
        onConfirm={(contact: any) => {
          setPickerOpen(null);
          if (contact) {
            handleUpdate({ reporterId: contact.id } as any);
          }
        }}
        config={{
          defaultValue: ticket.reporter ?? undefined,
          allowRemoval: true,
        }}
      />

      {/* Assign & notify modal */}
      <RenoAssignAndNotifyModal
        isOpen={isAssignOpen}
        ticketId={ticket.id}
        currentAssignedTo={ticket.assignedTo}
        onClose={() => setIsAssignOpen(false)}
        onConfirm={handleAssignAndNotify}
      />

      {/* Project part picker */}
      <UniversalPickerModal
        isOpen={pickerOpen === 'part'}
        mode="project-part"
        title="Projectonderdeel selecteren"
        onClose={() => setPickerOpen(null)}
        onConfirm={(part: any) => {
          setPickerOpen(null);
          handleUpdate({ projectPartId: part?.id ?? null } as any);
        }}
        config={{
          defaultValue: ticket.projectPart ?? undefined,
          allowRemoval: true,
          // `currentRenovationProjectId` — dat is de sleutel die de picker
          // uitleest. Hier stond `renovationProjectId`, een sleutel die niemand
          // las, dus kreeg de picker nooit het project van dit ticket: hij vroeg
          // je er eerst een te kiezen terwijl er al een gekozen was, en de
          // knop "verwijderen" hoorde bij die verkeerde stap.
          currentRenovationProjectId:
            ticket.renovationProjectId ?? ticket.renovationProject?.id,
        }}
      />

      {/* Supplier picker — voegt een leverancier toe (meerdere mogelijk) */}
      <UniversalPickerModal
        isOpen={pickerOpen === 'supplier'}
        mode="supplier"
        title="Leverancier toevoegen"
        onClose={() => setPickerOpen(null)}
        onConfirm={(company: any) => {
          setPickerOpen(null);
          if (company?.id) handleAddSupplier(company.id);
        }}
        // Onbekende leverancier kan hier meteen aangemaakt en toegevoegd worden.
        config={{ allowRemoval: false, allowCreateSupplier: true }}
      />

      {/* Delete confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ticket verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              RT-{ticket.id} "{ticket.title}" wordt permanent verwijderd. Dit kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
